import { useState } from "react";
import { api } from "@/lib/api";

/**
 * AIDetectorModal — verifica si un texto ha sido escrito por IA o a mano.
 *
 * Portado del repo público `Analisis_Textos_IA` con la paleta y tipografía
 * de Grabarte360 (brandbook: rojo #941620, verde #26775E, fondo #FAFAF6,
 * font-serif para titulares y uppercase tracking para labels).
 *
 * Muestra 3 capas de resultado:
 *  1) Score global 0-100 + veredicto (Humano / Mixto / IA)
 *  2) Resumen del análisis + métricas locales (burstiness, diversidad…)
 *  3) Highlighting frase a frase (color según probabilidad IA de cada frase)
 *
 * Acción secundaria: "Humanizar texto" → reescribe con Claude para que
 * suene más natural, y devuelve el resultado al textarea para re-analizarlo.
 */
function verdictMeta(v, p) {
  if (v === "ai" || p >= 65) {
    return { label: "Probablemente IA", cls: "text-[#941620]", bg: "bg-[#941620]/10", ring: "ring-[#941620]/40" };
  }
  if (v === "mixed" || p >= 32) {
    return { label: "Mixto / Dudoso", cls: "text-amber-700", bg: "bg-amber-100/60", ring: "ring-amber-400/50" };
  }
  return { label: "Probablemente Humano", cls: "text-[#26775E]", bg: "bg-[#26775E]/10", ring: "ring-[#26775E]/40" };
}

function sentenceClass(p) {
  if (p >= 70) return "bg-[#941620]/15 border-b-2 border-[#941620]/60";
  if (p >= 30) return "bg-amber-100 border-b-2 border-amber-400";
  return "bg-[#26775E]/10 border-b-2 border-[#26775E]/50";
}

export default function AIDetectorModal({ open, onClose }) {
  const [text, setText] = useState("");
  const [deep, setDeep] = useState(false);
  const [loading, setLoading] = useState(false);
  const [humanizing, setHumanizing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  if (!open) return null;

  const reset = () => {
    setText("");
    setDeep(false);
    setResult(null);
    setError("");
    setLoading(false);
    setHumanizing(false);
  };

  const closeAndReset = () => {
    if (loading || humanizing) return;
    reset();
    onClose?.();
  };

  const handleAnalyze = async () => {
    if (text.trim().length < 20) {
      setError("Escribe al menos 20 caracteres para analizar.");
      return;
    }
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/ai-detector/detect", { text, deep });
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al analizar el texto.");
    } finally {
      setLoading(false);
    }
  };

  const handleHumanize = async () => {
    setHumanizing(true);
    setError("");
    try {
      const { data } = await api.post("/ai-detector/humanize", { text });
      setText(data.humanized_text);
      setResult(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al humanizar el texto.");
    } finally {
      setHumanizing(false);
    }
  };

  const overall = result?.overall_ai_probability ?? 0;
  const meta = result ? verdictMeta(result.verdict, overall) : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={closeAndReset}
      data-testid="ai-detector-modal"
    >
      <div
        className="relative flex h-[92vh] w-[1000px] max-w-full flex-col overflow-hidden rounded-xl bg-[#FAFAF6] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <header className="flex items-center justify-between gap-4 border-b border-stone-200 bg-white px-5 py-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#941620]">
              Detector IA / Humano
            </p>
            <p className="mt-0.5 font-serif text-base text-stone-900">
              Comprueba si un texto ha sido escrito por IA o a mano
            </p>
          </div>
          <button
            onClick={closeAndReset}
            disabled={loading || humanizing}
            data-testid="ai-detector-close"
            className="rounded-full border border-stone-300 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-700 transition hover:border-[#941620] hover:text-[#941620] disabled:opacity-40"
          >
            ✕ Cerrar
          </button>
        </header>

        {/* BODY */}
        <div className="grid flex-1 gap-4 overflow-hidden p-5 md:grid-cols-[1.1fr_1fr]">
          {/* IZQUIERDA · INPUT */}
          <div className="flex flex-col gap-3 overflow-hidden">
            <label className="text-[10px] font-semibold uppercase tracking-[0.28em] text-stone-500">
              Texto a analizar
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Pega aquí el texto que quieres analizar…"
              disabled={loading || humanizing}
              data-testid="ai-detector-textarea"
              className="flex-1 resize-none rounded-lg border border-stone-300 bg-white p-3 font-serif text-[14px] leading-relaxed text-stone-800 outline-none transition focus:border-[#941620] focus:ring-1 focus:ring-[#941620]/30 disabled:opacity-60"
            />
            <div className="flex items-center justify-between gap-3 text-[11px] text-stone-500">
              <span>{text.trim().split(/\s+/).filter(Boolean).length} palabras · {text.length} caracteres</span>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deep}
                  onChange={(e) => setDeep(e.target.checked)}
                  disabled={loading || humanizing}
                  data-testid="ai-detector-deep"
                  className="h-3 w-3 accent-[#941620]"
                />
                <span className="uppercase tracking-widest font-semibold">Análisis profundo (2 pasadas)</span>
              </label>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAnalyze}
                disabled={loading || humanizing || text.trim().length < 20}
                data-testid="ai-detector-analyze"
                className="flex-1 rounded-full bg-[#941620] px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.24em] text-white transition hover:bg-[#7a1119] disabled:cursor-not-allowed disabled:opacity-40"
              >
                {loading ? "Analizando…" : "🔍 Analizar texto"}
              </button>
              <button
                onClick={handleHumanize}
                disabled={loading || humanizing || text.trim().length < 20}
                data-testid="ai-detector-humanize"
                title="Reescribe el texto para que suene más humano"
                className="rounded-full border border-stone-400 bg-white px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.24em] text-stone-700 transition hover:border-[#26775E] hover:text-[#26775E] disabled:cursor-not-allowed disabled:opacity-40"
              >
                {humanizing ? "…" : "✎ Humanizar"}
              </button>
            </div>
            {error && (
              <div className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-[12px] text-rose-800">
                {error}
              </div>
            )}
          </div>

          {/* DERECHA · RESULTADO */}
          <div className="flex flex-col gap-3 overflow-hidden">
            <label className="text-[10px] font-semibold uppercase tracking-[0.28em] text-stone-500">
              Resultado
            </label>
            <div className="flex-1 overflow-y-auto rounded-lg border border-stone-200 bg-white p-4">
              {!result && !loading && (
                <div className="flex h-full flex-col items-center justify-center text-center">
                  <div className="text-[3rem]">🤖</div>
                  <p className="mt-3 font-serif text-lg text-stone-800">
                    Pega un texto y pulsa <span className="text-[#941620]">Analizar</span>.
                  </p>
                  <p className="mt-2 max-w-xs text-[12px] leading-relaxed text-stone-500">
                    La IA (Claude Sonnet) + heurísticas locales devuelven un
                    porcentaje 0-100 y clasifican cada frase.
                  </p>
                </div>
              )}
              {loading && (
                <div className="flex h-full flex-col items-center justify-center text-center">
                  <div className="text-[3rem] animate-pulse">⋯</div>
                  <p className="mt-3 font-serif text-base text-stone-800">
                    {deep ? "Análisis profundo (2 pasadas)…" : "Analizando texto…"}
                  </p>
                </div>
              )}
              {result && meta && (
                <div className="flex flex-col gap-4" data-testid="ai-detector-result">
                  {/* GAUGE + VEREDICTO */}
                  <div className={`flex items-center gap-4 rounded-lg ${meta.bg} p-4 ring-1 ${meta.ring}`}>
                    <div className={`flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-white text-[24px] font-serif ${meta.cls} shadow-sm`}>
                      {overall}%
                    </div>
                    <div>
                      <p className={`text-[10px] font-semibold uppercase tracking-[0.28em] ${meta.cls}`}>
                        {meta.label}
                      </p>
                      <p className="mt-1 text-[12px] leading-relaxed text-stone-700">
                        {result.summary}
                      </p>
                    </div>
                  </div>

                  {/* MÉTRICAS */}
                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    <div className="rounded border border-stone-200 bg-stone-50 px-2.5 py-1.5">
                      <p className="uppercase tracking-widest text-stone-500">LLM</p>
                      <p className="font-serif text-stone-900">{result.llm_score}%</p>
                    </div>
                    <div className="rounded border border-stone-200 bg-stone-50 px-2.5 py-1.5">
                      <p className="uppercase tracking-widest text-stone-500">Heurística</p>
                      <p className="font-serif text-stone-900">{result.heuristic_score}%</p>
                    </div>
                    <div className="rounded border border-stone-200 bg-stone-50 px-2.5 py-1.5">
                      <p className="uppercase tracking-widest text-stone-500">Burstiness</p>
                      <p className="font-serif text-stone-900">{result.metrics.burstiness}</p>
                    </div>
                    <div className="rounded border border-stone-200 bg-stone-50 px-2.5 py-1.5">
                      <p className="uppercase tracking-widest text-stone-500">Diversidad léx.</p>
                      <p className="font-serif text-stone-900">{result.metrics.lexical_diversity}</p>
                    </div>
                  </div>

                  {/* HEATMAP FRASE A FRASE */}
                  {result.sentences?.length > 0 && (
                    <div>
                      <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-stone-500">
                        Frase a frase
                      </p>
                      <div className="rounded-lg border border-stone-200 bg-stone-50 p-3 font-serif text-[13px] leading-relaxed text-stone-900" data-testid="ai-detector-sentences">
                        {result.sentences.map((s, i) => (
                          <span
                            key={i}
                            className={`${sentenceClass(s.ai_probability)} mr-0.5 px-1 rounded-sm`}
                            title={`${s.ai_probability}% IA`}
                          >
                            {s.text}{" "}
                          </span>
                        ))}
                      </div>
                      <div className="mt-2 flex items-center gap-3 text-[10px] uppercase tracking-widest text-stone-500">
                        <span className="flex items-center gap-1">
                          <span className="h-2 w-4 rounded bg-[#26775E]/40" />
                          Humano
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="h-2 w-4 rounded bg-amber-300" />
                          Dudoso
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="h-2 w-4 rounded bg-[#941620]/50" />
                          IA
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
